<template>
  <div class="materialIndexAdd">
    <h1 class="page-title">{{ title }}</h1>

    <v-form ref="form" lazy-validation>
      <div class="cols">
        <!-- LEFT COLUMN -->
        <div class="col">
          <!-- 1. 기본 정보 -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>기본 정보</div>
            <div class="grid2">
              <div class="field">
                <label>자재코드</label>
                <v-text-field ref="codeField" outlined dense :placeholder="isEdit ? '' : '저장 시 자동 생성'" v-model="param.code" readonly disabled hide-details="auto"></v-text-field>
              </div>
              <div class="field">
                <label>원료약어 <em class="required">*</em></label>
                <v-text-field ref="rmavField" outlined dense placeholder="예: EC, DMC, PS" v-model="param.rmav" :readonly="isEdit" :disabled="isEdit" :rules="[requiredRule('원료약어를 입력해주세요')]" hide-details="auto"></v-text-field>
              </div>
              <div class="field full">
                <label>화학물질명 <em class="required">*</em></label>
                <v-text-field ref="chemicalNameField" outlined dense placeholder="예: 1,3-Dioxolan-2-one" v-model="param.chemical_name" :rules="[requiredRule('화학물질명을 입력해주세요')]" hide-details="auto"></v-text-field>
              </div>
              <div class="field full">
                <label>관용명 및 이명</label>
                <v-text-field outlined dense placeholder="예: (Ethylene Carbonate)" v-model="param.common_name" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>CAS No.</label>
                <v-text-field outlined dense placeholder="예: 96-49-1" v-model="param.cas_no" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>EC No.</label>
                <v-text-field outlined dense placeholder="예: 202-510-0" v-model="param.ec_no" v-mask="'###-###-#'" hide-details></v-text-field>
              </div>
            </div>
          </section>

          <!-- 2. 물성 · 분석 특성 -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>물성 · 분석 특성</div>
            <div class="grid2">
              <div class="field">
                <label>분자량</label>
                <v-text-field outlined dense placeholder="예: 88.06 (소수 2자리)" v-model="param.mw" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>초기 끓는점</label>
                <v-text-field outlined dense placeholder="예: 248 (단위 °C)" v-model="param.init_boiling_p" hide-details></v-text-field>
              </div>
            </div>
            <div class="sub">분석 사유</div>
            <div class="toggle-grid">
              <div class="tfield single full-row">
                <v-switch :class="['yn-switch', 'gc-switch', { 'is-yes': param.gc_analy_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.gc_analy_yn"></v-switch>
                <span class="tlabel">GC 분석가능</span>
              </div>
            </div>
          </section>

          <!-- 3. 유독물질 · 법정 기준 · 유해성 한도 (유독물질 여부에 따라 컬럼 표시) -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>유독물질 · 법정 기준 · 유해성 한도 (화관법)</div>
            <div class="toggle-grid" style="margin-bottom: 16px">
              <div class="tfield single full-row">
                <v-switch :class="['yn-switch', { 'is-yes': param.toxic_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.toxic_subs_yn" @change="onToxicYnChange"></v-switch>
                <span class="tlabel">유독물질 여부</span>
              </div>
            </div>
            <div class="toxic-hint" v-if="param.toxic_subs_yn !== 'Y'">유독물질 여부를 'Y'로 설정하면 법정 기준 · 유해성 한도 항목을 입력할 수 있습니다.</div>
            <div class="grid2" v-if="param.toxic_subs_yn === 'Y'">
              <div class="field">
                <label>유독물질 번호</label>
                <v-text-field outlined dense placeholder="예: 2013-1-668" v-model="param.toxic_no" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>인체급성유해성물질 법정기준함량 (wt%)</label>
                <v-text-field outlined dense placeholder="예: 10" v-model="param.human_acute_threshold" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>인체만성유해성물질 법정기준함량 (wt%)</label>
                <v-text-field outlined dense placeholder="예: 0.1" v-model="param.human_chronic_threshold" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>생태유해성물질 법정기준함량 (wt%)</label>
                <v-text-field outlined dense placeholder="예: 25" v-model="param.eco_threshold" hide-details></v-text-field>
              </div>
            </div>
          </section>

          <!-- 7. 위험물안전 관리법 -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>위험물안전 관리법</div>
            <div class="grid2">
              <div class="field full">
                <label>위험물안전관리법 (소방청) 분류</label>
                <v-text-field outlined dense placeholder="예: 제4류 인화성액체 제1석유류(비수용성) / 비위험물" v-model="param.dangerous_goods_fire_agency" hide-details></v-text-field>
              </div>
              <div class="field full">
                <label>위험물안전관리법 (안전보건공단) 분류 + 지정수량</label>
                <v-text-field outlined dense placeholder="예: 제4류 제1석유류(비수용성), 200L" v-model="param.dangerous_goods_kosha" hide-details></v-text-field>
              </div>
            </div>
          </section>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="col">
          <!-- 5. 화학물질 규제 분류 -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>화학물질 규제 분류 (화관법 · 화평법)</div>
            <div class="sub">해당 여부</div>
            <div class="toggle-grid">
              <div class="tfield">
                <span class="tlabel">허가물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.permitted_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.permitted_subs_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">제한물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.restricted_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.restricted_subs_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">금지물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.prohibited_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.prohibited_subs_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">허가대상물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.permission_target_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.permission_target_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">신규물질 여부</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.new_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.new_subs_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">대량생산화학물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.high_volume_chem_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.high_volume_chem_yn"></v-switch>
              </div>
            </div>
            <div class="sub">분류 정보</div>
            <div class="grid2">
              <div class="field">
                <label>기존물질</label>
                <v-text-field outlined dense placeholder="예: KE-12028" v-model="param.ex_subs" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>등록대상 기존화학물질 (번호)</label>
                <v-text-field outlined dense placeholder="예: 181" v-model="param.registered_existing_chem_no" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>사고대비물질 번호</label>
                <v-text-field outlined dense placeholder="예: 32" v-model="param.accident_prep_no" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>사고대비물질 함유기준(%)</label>
                <v-text-field outlined dense placeholder="예: 25" v-model="param.accident_prep_legal" hide-details></v-text-field>
              </div>
              <div class="field">
                <label>중점관리물질</label>
                <v-text-field outlined dense placeholder="예: 별표-177(CMR)" v-model="param.priority_mgmt_subs" hide-details></v-text-field>
              </div>
            </div>
          </section>

          <!-- 6. 산업안전보건 -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>산업안전보건 (산안법)</div>
            <div class="grid2">
              <div class="field">
                <label>작업환경측정 대상 유해인자 주기</label>
                <v-select outlined dense :items="settings.cycleOptionsMap.work_env_measure_cycle" v-model="param.work_env_measure_cycle" hide-details></v-select>
              </div>
              <div class="field">
                <label>특수건강진단 대상 유해인자 주기</label>
                <v-select outlined dense :items="settings.cycleOptionsMap.special_health_check_cycle" v-model="param.special_health_check_cycle" hide-details></v-select>
              </div>
            </div>
            <div class="sub">해당 여부</div>
            <div class="toggle-grid">
              <div class="tfield">
                <span class="tlabel">관리대상 유해물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.managed_hazard_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.managed_hazard_subs_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">노출기준 설정물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.exposure_std_subs_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.exposure_std_subs_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">허용기준 이하 유지 대상</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.allowed_threshold_maintain_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.allowed_threshold_maintain_yn"></v-switch>
              </div>
              <div class="tfield">
                <span class="tlabel">PSM 제출 대상</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.psm_target_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.psm_target_yn"></v-switch>
              </div>
              <div class="tfield full-row">
                <span class="tlabel">영업비밀 인정제외 물질</span>
                <v-switch :class="['yn-switch', { 'is-yes': param.trade_secret_excluded_yn === 'Y' }]" inset hide-details true-value="Y" false-value="N" v-model="param.trade_secret_excluded_yn"></v-switch>
              </div>
            </div>
          </section>

          <!-- 8. 국제 규제 · 비고 -->
          <section class="card">
            <div class="card-head"><span class="bar"></span>국제 규제 · 비고</div>
            <div class="grid2">
              <div class="field full">
                <label>EU REACH SVHC 후보물질</label>
                <v-text-field outlined dense placeholder="예: 1,3-propanesultone, No.164, 등재일자: 2015-12-17" v-model="param.eu_reach_svhc" hide-details></v-text-field>
              </div>
              <div class="field full">
                <label>특이사항</label>
                <v-text-field outlined dense placeholder="예: 저온보관" v-model="param.special_note" hide-details></v-text-field>
              </div>
            </div>
          </section>
        </div>
      </div>
    </v-form>

    <div class="actionbar">
      <div class="btn-group">
        <v-btn class="btn" depressed @click="$emit('cancel')">취소</v-btn>
        <v-btn class="btn primary" depressed @click="onSave">{{ saveLabel }}</v-btn>
      </div>
    </div>
  </div>
</template>
<script>
import * as materialIndex from '@/assets/grid/materialIndex';
export default {
  name: 'MaterialIndexForm',
  props: {
    param: { type: Object, required: true },
    title: { type: String, default: '물질 인덱스 등록' },
    saveLabel: { type: String, default: '저장' },
    isEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      settings: materialIndex,
    };
  },
  methods: {
    // 유독물질 여부를 사용자가 N(=Y 아님)으로 바꾸면 이 섹션의 숨겨진 컬럼 값 전부 초기화
    // (로드 시점이 아닌 사용자 토글 시에만 동작하도록 @change 사용)
    onToxicYnChange(val) {
      if (val !== 'Y') {
        this.param.toxic_no = '';
        this.param.human_acute_threshold = '';
        this.param.human_chronic_threshold = '';
        this.param.eco_threshold = '';
      }
    },
    validate() {
      return this.$refs.form.validate();
    },
    requiredRule(message) {
      return v => (v !== null && v !== undefined && String(v).trim() !== '') || message;
    },
    onSave() {
      if (this.validate()) {
        this.$emit('save');
      } else {
        this.focusFirstEmpty();
        this.$emit('invalid');
      }
    },
    focusFirstEmpty() {
      const checks = [
        { ref: 'rmavField', value: this.param.rmav },
        { ref: 'chemicalNameField', value: this.param.chemical_name },
      ];
      for (const c of checks) {
        const v = c.value;
        if (v == null || String(v).trim() === '') {
          const target = this.$refs[c.ref];
          if (target && typeof target.focus === 'function') {
            this.$nextTick(() => target.focus());
          }
          return;
        }
      }
    },
  },
};
</script>
<style lang="scss">
.materialIndexAdd {
  $green: #1f7a44;
  $green-dark: #185e34;
  $green-bar: #5aa84f;
  $bg: #f4f6f7;
  $card: #ffffff;
  $border: #e4e7ea;
  $border-in: #d6dadf;
  $text: #2a2e33;
  $label: #3b424a;
  $muted: #8a9098;
  $radius: 10px;
  $radius-in: 7px;

  background: $bg;
  color: $text;
  padding: 26px 30px 110px;

  > .page-title,
  > .v-form {
    width: 80%;
    margin-left: auto;
    margin-right: auto;
  }

  .page-title {
    font-size: 21px;
    font-weight: 700;
    margin: 4px auto 24px;
    color: #23272c;
  }

  .cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 22px;
    align-items: start;
  }

  .col {
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  .card {
    background: $card;
    border: 1px solid $border;
    border-radius: $radius;
    padding: 20px 24px 24px;
  }

  .card-head {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 16px;
    font-weight: 700;
    color: #262b30;
    margin-bottom: 18px;

    .bar {
      width: 4px;
      height: 17px;
      background: $green-bar;
      border-radius: 2px;
    }
  }

  .sub {
    font-size: 12px;
    font-weight: 500;
    color: $muted;
    margin: 18px 0 10px;
  }

  .toxic-hint {
    font-size: 13px;
    color: $muted;
    padding: 14px 4px 6px;
    line-height: 1.5;
  }

  .grid2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 20px;
  }

  .field {
    min-width: 0;

    &.full {
      grid-column: 1 / -1;
    }

    label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: $label;
      margin-bottom: 7px;

      .required {
        color: #d64545;
        font-style: normal;
        margin-left: 2px;
      }
    }

    .v-input {
      margin: 0;
      padding: 0;
    }

    .v-text-field--outlined,
    .v-select.v-text-field--outlined,
    .v-textarea.v-text-field--outlined {
      fieldset {
        border-color: $border-in;
        border-radius: $radius-in;
      }

      &.v-input--is-focused fieldset {
        border-color: $green;
        border-width: 1px;
      }

      &.error--text fieldset {
        border-color: #d64545 !important;
        border-width: 1px !important;
      }
      &.error--text .v-messages__message {
        color: #d64545;
      }

      .v-input__slot {
        min-height: 44px;
      }

      input,
      textarea {
        font-size: 13.5px;
      }

      &.v-input--is-disabled {
        .v-input__slot {
          background: #ececec;
        }
        input,
        textarea {
          color: #2a2e33 !important;
        }
      }
    }
  }

  .toggle-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border: 1px solid $border;
    border-radius: $radius-in;
    overflow: hidden;
  }

  .tfield {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
    min-height: 50px;
    padding: 0 16px;
    border-top: 1px solid $border;
    width: 100%;

    &:nth-child(odd) {
      border-right: 1px solid $border;
    }

    &:nth-child(1),
    &:nth-child(2) {
      border-top: none;
    }

    &.full-row {
      grid-column: 1 / -1;
      border-right: none !important;
    }

    &.single {
      grid-template-columns: auto 1fr;
      gap: 14px;
    }

    .tlabel {
      font-size: 13.5px;
      color: $label;
      line-height: 1.25;
      white-space: nowrap;
    }

    .yn-switch {
      justify-self: end;
    }
  }

  .yn-switch {
    margin: 0 !important;
    padding: 0 !important;
    flex: 0 0 auto;

    .v-input__slot {
      margin: 0 !important;
    }

    .v-input--switch__track {
      opacity: 1 !important;
      background-color: #c6ccd2 !important;

      &::after {
        content: 'N';
        position: absolute;
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        pointer-events: none;
      }
    }

    .v-input--switch__thumb {
      color: #fff !important;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
    }

    &.is-yes {
      .v-input--switch__track {
        background-color: $green !important;

        &::after {
          content: 'Y';
          left: 8px;
          right: auto;
        }
      }
    }

    /* GC분석가능여부: 디폴트가 Y(정상), N이 특이사항 → 색만 반전(N=초록, Y=회색). 라벨은 값 그대로 */
    &.gc-switch {
      .v-input--switch__track {
        background-color: $green !important;
      }
      &.is-yes .v-input--switch__track {
        background-color: #c6ccd2 !important;
      }
    }
  }

  .actionbar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    border-top: 1px solid $border;
    padding: 14px 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 5;

    .btn-group {
      display: inline-flex;
      gap: 10px;
    }

    .btn {
      flex: 0 0 auto;
      min-width: 0 !important;
      width: auto !important;
      height: 42px;
      padding: 0 26px;
      border-radius: $radius-in;
      font-size: 14px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
      background: #fff !important;
      border: 1px solid $border-in;
      color: #54595f;
      box-shadow: none !important;

      &:hover {
        background: #f3f5f6 !important;
      }

      &.primary {
        background: $green !important;
        border-color: $green !important;
        color: #fff !important;

        &:hover {
          background: $green-dark !important;
        }
      }
    }
  }

  @media (max-width: 900px) {
    .cols {
      grid-template-columns: 1fr;
    }
    .grid2,
    .toggle-grid {
      grid-template-columns: 1fr;
    }
    .tfield:nth-child(odd) {
      border-right: none;
    }
    .tfield:nth-child(2) {
      border-top: 1px solid $border;
    }
  }
}
</style>
